import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "react-toastify";

const formSchema = z.object({
  firstName: z.string().min(1, { message: "Le nom est requis." }).optional(),
  lastName: z.string().min(1, { message: "Le prénom est requis." }).optional(),
  email: z.string().email({ message: "Doit être un email valide." }),
  clinic: z.string().min(1, { message: "La clinique est requise" }).optional(),
  role: z.enum(["PRATICIEN", "ADMIN"]),
});

const FormNewAccount: React.FC = () => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      clinic: "",
      role: "PRATICIEN" as const,
    },
  });

  type FormData = z.infer<typeof formSchema>;

  const handleSave: SubmitHandler<FormData> = async (data) => {
    try {
      await axios.post("/dashboard/user", data).catch((error) => {
        console.error("Erreur lors de la création de l'utilisateur:", error);
      });
      toast(`Le compte rattaché à l'email : ${data.email} a bien été créé`, {
        position: "top-center",
        autoClose: 1000,
      });
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur:", error);
      toast.error(
        `Erreur lors de la création du compte rattaché à l'email : ${data.email}`
      );
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <div className="flex justify-around mb-6 mt-6">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-1/4">
                <FormLabel>Nom</FormLabel>
                <FormControl>
                  <Input placeholder="Nom" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-1/4">
                <FormLabel>Prénom</FormLabel>
                <FormControl>
                  <Input placeholder="Prenom" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-1/4">
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input placeholder="nom@mail.fr" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-around mb-6 mt-6">
          <FormField
            control={form.control}
            name="clinic"
            render={({ field }) => (
              <FormItem className="w-1/4">
                <FormLabel>Etablissement</FormLabel>
                <FormControl>
                  <Input placeholder="Clinique" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem className="w-1/4">
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="Praticien" value="PRATICIEN">
                        Praticien
                      </SelectItem>
                      <SelectItem key="Administrateur" value="ADMIN">
                        Administateur
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="w-1/4"></div>
        </div>
        <div className="w-full flex justify-center">
          <Button type="submit" className="mt-6">
            <UserPlus className="mr-2" />
            Créer le compte
          </Button>
        </div>
      </form>
    </Form>
  );
};
export default FormNewAccount;
